import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faBars, faBell, faChevronLeft, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navbar } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';

import AppNavSideBar from 'components/layout/sidenav/AppSideNavBar';
import { brightRed, deeplavender, white } from 'lib/css/colors';
import { NAVIGATION_SIDE_BAR_WIDTH, SIDE_BAR_DISABLED_PATHS } from 'lib/constants';
import pointerOnHover from 'lib/css/pointerOnHover';
import useGet from 'lib/hooks/useGet';
import { USER_GET_ALL_NOTIFICATIONS } from 'lib/networking/endpoints';
import UserNotificationsModal from 'components/user_notifications/UserNotificationsModal';

const AppNavBarContext = React.createContext(null);

function AppNavBar({ expanded, setExpanded, action = 'menu', title = '' }) {
  const location = useLocation();
  const history = useHistory();

  const { data: { count } = {} } = useGet(USER_GET_ALL_NOTIFICATIONS, {});

  const [showNotifications, setShowNotifications] = useState(false);

  const handleShowNotifications = () => setShowNotifications(true);
  const handleCloseNotifications = () => setShowNotifications(false);

  useEffect(() => {
    if (SIDE_BAR_DISABLED_PATHS.includes(location.pathname)) {
      setExpanded(false);
    }
  }, [location, setExpanded]);

  const brandClickHandler = useCallback(
    action => {
      if (action === 'back') {
        history.goBack();
      } else if (action === 'menu') {
        setExpanded(!expanded);
      }
    },
    [expanded, setExpanded, history],
  );

  return (
    <>
      <Navbar
        sticky="top"
        collapseOnSelect
        expand="lg"
        bg="white"
        variant="light"
        className={classNames(css(expanded ? styles.navbarExpanded : styles.navbarCollapsed))}
      >
        <Navbar.Brand className="font-weight-bold">
          <div className="d-flex">
            <div
              onClick={() => brandClickHandler(action)}
              className={classNames(
                css(expanded && styles.active),
                'nav-menu-btn align-self-center',
              )}
            >
              <FontAwesomeIcon className="mr-2" icon={action === 'back' ? faChevronLeft : faBars} />
              Menu
            </div>
            {!expanded && <span className={classNames(css(styles.title), 'mx-3')}>{title}</span>}
          </div>
        </Navbar.Brand>
        <div className="ml-auto d-flex align-items-center">
          <div className={css(styles.iconContainer)}>
            <FontAwesomeIcon
              icon={faClock}
              size="lg"
              onClick={() => history.push('/time-logs')}
              color={deeplavender}
              className={pointerOnHover}
            />
          </div>
          <div className={css(styles.iconContainer)}>
            <FontAwesomeIcon
              icon={faBell}
              size="lg"
              onClick={handleShowNotifications}
              className={pointerOnHover}
            />
            {count > 0 && <span className={css(styles.badge)}>{count}</span>}
          </div>
        </div>
      </Navbar>
      {action === 'menu' && <AppNavSideBar expanded={expanded} />}
      <UserNotificationsModal show={showNotifications} handleClose={handleCloseNotifications} />
    </>
  );
}

const styles = StyleSheet.create({
  active: {
    color: deeplavender,
  },
  navbarCollapsed: {
    maxHeight: 40,
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    zIndex: 1040,
  },
  navbarExpanded: {
    marginLeft: NAVIGATION_SIDE_BAR_WIDTH,
    width: `calc(100% - ${NAVIGATION_SIDE_BAR_WIDTH})`,
    maxHeight: 40,
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    zIndex: 1040,
  },
  title: {
    fontSize: 25,
    fontWeight: 'bolder',
    lineHeight: 1,
  },
  iconContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.5rem',
  },
  badge: {
    position: 'absolute',
    top: '-5px',
    right: '-10px',
    backgroundColor: brightRed,
    color: white,
    borderRadius: '50%',
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '1.1rem',
    width: '1.1rem',
  },
});

AppNavBar.propTypes = {
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func,
  action: PropTypes.oneOf(['back', 'menu']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default AppNavBar;
export { AppNavBarContext };
